import React, { useCallback, useEffect, useState } from "react";
import {
  Flag, Loader2, RefreshCw, ShieldX, Check, ChevronRight, X,
  ShieldAlert, Bug, Cog, FileWarning, FileX, MessageSquareWarning,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REASON_META = {
  broken:        { label: "Broken",     Icon: Cog,                  accent: "#FFD700" },
  malware:       { label: "Malware",    Icon: ShieldAlert,          accent: "#EF4444" },
  fake:          { label: "Fake",       Icon: FileX,                accent: "#FF6B35" },
  wrong_content: { label: "Wrong",      Icon: FileWarning,          accent: "#6D4AFF" },
  low_quality:   { label: "Low qual.",  Icon: Bug,                  accent: "#22C77A" },
  other:         { label: "Other",      Icon: MessageSquareWarning, accent: "#FF2A6D" },
};

function fmtAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function ReportDetail({ hash, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/admin/torrents/reports/${hash}`);
        setData(d);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [hash]);

  return (
    <div
      data-testid="admin-report-detail-backdrop"
      className="fixed inset-0 z-[55] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-report-detail"
        className="w-full max-w-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-[var(--tt-border)] flex items-center gap-3">
          <Flag className="w-4 h-4 text-[var(--tt-primary)]" />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg font-extrabold">Report details</h3>
            <div className="text-[10px] font-mono-tt text-[var(--tt-text-muted)] truncate">{hash}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 text-[var(--tt-text-muted)] hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-[var(--tt-text-muted)] text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : !data || (data.reports || []).length === 0 ? (
            <div className="text-sm text-[var(--tt-text-muted)] text-center py-8">No reports.</div>
          ) : (
            <ul className="space-y-3" data-testid="admin-report-detail-list">
              {data.reports.map((r, idx) => {
                const meta = REASON_META[r.reason] || REASON_META.other;
                return (
                  <li
                    key={idx}
                    className={`p-3 rounded-2xl border ${r.resolved ? "border-emerald-500/30 bg-emerald-500/5 opacity-70" : "border-[var(--tt-border)] bg-black/20"}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${meta.accent}22`, color: meta.accent }}
                      >
                        <meta.Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: meta.accent }}>
                            {meta.label}
                          </span>
                          {r.resolved && (
                            <span className="text-[9px] uppercase font-bold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">
                              Resolved
                            </span>
                          )}
                          <span className="ml-auto text-[10px] text-[var(--tt-text-muted)] font-mono-tt">
                            {fmtAgo(r.created_at)}
                          </span>
                        </div>
                        {r.reporter_name && (
                          <div className="text-[11px] text-[var(--tt-text-muted)] mt-0.5">
                            by <span className="text-white/80">{r.reporter_name}</span>
                          </div>
                        )}
                        {r.note && (
                          <div className="text-xs text-white/80 mt-1.5 italic leading-relaxed">
                            &ldquo;{r.note}&rdquo;
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminReports() {
  const [rows, setRows] = useState([]);
  const [threshold, setThreshold] = useState(5);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [openHash, setOpenHash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/torrents/reports", { params: { status: "open" } });
      // Backend now returns { rows, auto_hide_threshold }
      if (Array.isArray(data)) {
        setRows(data);
      } else {
        setRows(data?.rows || []);
        if (typeof data?.auto_hide_threshold === "number") setThreshold(data.auto_hide_threshold);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolveHash = async (hash) => {
    setBusy(hash);
    try {
      await api.post(`/admin/torrents/reports/${hash}/resolve`);
      toast.success("Reports resolved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resolve failed");
    } finally {
      setBusy(null);
    }
  };

  const blockHash = async (row) => {
    setBusy(row.info_hash);
    try {
      await api.post("/admin/torrents/block", {
        info_hash: row.info_hash,
        name: row.name || "",
        reason: "Multiple user reports",
      });
      await api.post(`/admin/torrents/reports/${row.info_hash}/resolve`);
      toast.success("Torrent blocked & reports resolved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Block failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mt-10" data-testid="admin-reports-section">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Flag className="w-5 h-5 text-[var(--tt-primary)]" />
          Torrent Reports
        </h2>
        <span
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${
            rows.length > 0
              ? "bg-[var(--tt-primary)]/15 text-[var(--tt-primary)]"
              : "bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)]"
          }`}
        >
          {rows.length} open
        </span>
        <span
          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold bg-[var(--tt-yellow)]/15 text-[var(--tt-yellow)]"
          title="Hashes that hit this many open reports are auto-hidden from public listings"
        >
          Auto-hide ≥{threshold}
        </span>
        <button
          onClick={load}
          data-testid="admin-reports-refresh"
          className="ml-auto p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-[var(--tt-text-muted)] hover:text-white"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-12 flex items-center justify-center text-[var(--tt-text-muted)] text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reports…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-[var(--tt-text-muted)] text-sm">
            No open reports. The community trusts you. 🧹
          </div>
        ) : (
          <div className="divide-y divide-[var(--tt-border)]" data-testid="admin-reports-list">
            {rows.map((r) => {
              const top = Object.entries(r.reasons || {}).sort((a, b) => b[1] - a[1]).slice(0, 4);
              const isBusy = busy === r.info_hash;
              return (
                <div
                  key={r.info_hash}
                  data-testid={`admin-report-row-${r.info_hash}`}
                  className={`px-4 py-3 flex items-start gap-3 hover:bg-white/[0.02] ${
                    r.auto_hidden ? "bg-[var(--tt-yellow)]/[0.04]" : ""
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5 text-center w-12">
                    <div className={`font-display text-2xl font-extrabold leading-none ${
                      r.auto_hidden ? "text-[var(--tt-yellow)]" : "text-[var(--tt-primary)]"
                    }`}>
                      {r.open_count}
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-[var(--tt-text-faint)] mt-0.5">
                      reports
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold truncate max-w-md" title={r.name || r.info_hash}>
                        {r.name || <span className="font-mono-tt text-xs">{r.info_hash}</span>}
                      </span>
                      {r.media_type && (
                        <span className="text-[9px] uppercase font-bold bg-white/5 text-white/60 px-1.5 py-0.5 rounded">
                          {r.media_type}
                        </span>
                      )}
                      {r.auto_hidden && (
                        <span
                          data-testid={`admin-report-autohidden-${r.info_hash}`}
                          className="inline-flex items-center gap-1 text-[9px] uppercase font-bold bg-[var(--tt-yellow)]/15 text-[var(--tt-yellow)] border border-[var(--tt-yellow)]/40 px-1.5 py-0.5 rounded"
                          title="Hidden from public listings until you Block or Resolve"
                        >
                          <ShieldX className="w-2.5 h-2.5" /> Auto-hidden
                        </span>
                      )}
                    </div>
                    <div className="font-mono-tt text-[10px] text-[var(--tt-text-muted)] truncate mt-0.5">
                      {r.info_hash}
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {top.map(([reason, count]) => {
                        const meta = REASON_META[reason] || REASON_META.other;
                        return (
                          <span
                            key={reason}
                            data-testid={`admin-report-reason-${r.info_hash}-${reason}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                            style={{
                              color: meta.accent,
                              background: `${meta.accent}14`,
                              border: `1px solid ${meta.accent}44`,
                            }}
                          >
                            <meta.Icon className="w-2.5 h-2.5" />
                            {meta.label}
                            <span className="font-mono-tt text-white/60">×{count}</span>
                          </span>
                        );
                      })}
                      <span className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt ml-1">
                        first {fmtAgo(r.first_report)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => setOpenHash(r.info_hash)}
                      data-testid={`admin-report-view-${r.info_hash}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white/80"
                      title="View individual reports"
                    >
                      View <ChevronRight className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => blockHash(r)}
                      disabled={isBusy}
                      data-testid={`admin-report-block-${r.info_hash}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-400 disabled:opacity-50"
                      title="Block this torrent everywhere and resolve all reports"
                    >
                      <ShieldX className="w-3 h-3" /> Block
                    </button>
                    <button
                      onClick={() => resolveHash(r.info_hash)}
                      disabled={isBusy}
                      data-testid={`admin-report-resolve-${r.info_hash}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 disabled:opacity-50"
                      title="Mark all reports as resolved (without blocking)"
                    >
                      <Check className="w-3 h-3" /> Resolve
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {openHash && <ReportDetail hash={openHash} onClose={() => setOpenHash(null)} />}
    </section>
  );
}
